import { useMemo, useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import {
  SavedRouteAlert,
  useGetSavedRouteAlertsQuery,
} from 'state/emailAlertsSlices';
import { SavedRouteList, useGetSavedRoutesQuery } from 'state/savedRouteSlices';

import LoadingSpinner from 'features/spinner/Spinner';
import { secondary_light_text_color } from 'theme/cemTheme';

import { NotificationsPanel } from './NotificationsPanel';
import { RouteAccordion } from './RouteAccordion';
import { RouteMapDisplay } from './alertFields/RouteMapDisplay';
import { NoResults, NoRoutes } from './components/EmptyState';
import { FilterSearchBar } from './components/FilterSearchBar';
import { useNotificationProject } from './useNotificationProject';

import styles from './AssignRoutesPage.module.css';

const ROUTE_LIST_SPACING = '0.125rem';

export function AssignRoutesPage() {
  const { projectSlug: project_slug, navigateTo } = useNotificationProject();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRouteId, setExpandedRouteId] = useState<string | false>(false);
  const [hoveredRouteId, setHoveredRouteId] = useState<string | null>(null);

  const { currentData: savedRouteData, isLoading: isLoadingRoutes } =
    useGetSavedRoutesQuery(project_slug!, {
      skip: !project_slug,
    });

  const { data: allAlerts } = useGetSavedRouteAlertsQuery(project_slug!, {
    skip: !project_slug,
  });

  const filteredRoutes: SavedRouteList = useMemo(() => {
    const routes = savedRouteData?.savedRoutes || [];

    if (!searchQuery.trim()) {
      return routes;
    }

    const query = searchQuery.toLowerCase();
    return routes.filter((route) =>
      route.routeName.toLowerCase().includes(query),
    );
  }, [savedRouteData, searchQuery]);

  const hasAnyRoutes = (savedRouteData?.savedRoutes?.length ?? 0) > 0;

  const getAlertsForRoute = (routeId: string): SavedRouteAlert[] => {
    if (!allAlerts) return [];
    return allAlerts.filter((alert) =>
      alert.routes.some((route) => route.id === routeId),
    );
  };

  const handleAddAlert = (routeId: string) => {
    navigateTo.assignRoute(routeId);
  };

  return (
    <NotificationsPanel>
      {!project_slug || isLoadingRoutes ? (
        <Stack height="100%" alignItems="center" justifyContent="center">
          <LoadingSpinner large />
        </Stack>
      ) : (
        <Stack
          direction="row"
          spacing="0.25rem"
          className={styles.pageContainer}
        >
          <Stack className={styles.routesListContainer}>
            <Box py="0.625rem">
              <Typography variant="h3" color={secondary_light_text_color}>
                Route Alerts
              </Typography>
            </Box>
            <FilterSearchBar
              searchTerm={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by route name"
              hasFrame
            />
            {filteredRoutes.length === 0 ? (
              hasAnyRoutes ? (
                <NoResults resourceName="routes" />
              ) : (
                <NoRoutes />
              )
            ) : (
              <Stack spacing={ROUTE_LIST_SPACING} p="0.25rem">
                {filteredRoutes.map((route) => {
                  const routeAlerts = getAlertsForRoute(route.id);
                  return (
                    <RouteAccordion
                      key={route.id}
                      route={route}
                      alerts={routeAlerts}
                      isExpanded={expandedRouteId === route.id}
                      onExpand={(isExpanded) =>
                        setExpandedRouteId(isExpanded ? route.id : false)
                      }
                      onHover={(routeId) => {
                        setHoveredRouteId(routeId ? route.id : null);
                      }}
                      onAddAlert={() => handleAddAlert(route.id)}
                    />
                  );
                })}
              </Stack>
            )}
          </Stack>
          <Box className={styles.mapContainer}>
            <RouteMapDisplay
              selectedRoutes={expandedRouteId ? [expandedRouteId] : []}
              availableRoutes={filteredRoutes.map((route) => route.id)}
              hoveredRoutes={
                // no need to highlight the selected route
                hoveredRouteId && hoveredRouteId !== expandedRouteId
                  ? [hoveredRouteId]
                  : []
              }
            />
          </Box>
        </Stack>
      )}
    </NotificationsPanel>
  );
}
